import React, { useEffect, useRef, useState } from "react";
import { useTranslation } from "react-i18next";

import { Checkbox, Typography } from "@mui/material";
import ClickAwayListener from "@mui/material/ClickAwayListener";
import ListItemButton from "@mui/material/ListItemButton";
import MoreVertIcon from "@mui/icons-material/MoreVert";

import Search_Icon from "../../assets/icon/search.png";
import Button from "../../components/Button";
import { type CrewBase, type CrewContext, type UserContext } from "../../api/crew";
import useCrew from "../../hooks/useCrew";
import useUser from "../../hooks/useUser";

import { StyledList, StyledSearch } from "./styles";

type Base = CrewBase | { id: "all"; name: string };

type Crew = CrewContext & {
  baseIds?: number[] | null;
  baseName?: string | null;
};

interface SelectMenuOption {
  id?: number | string;
  name: string;
  subscription?: string;
  [key: string]: unknown;
}

interface SelectMenuProps<T extends SelectMenuOption> {
  search?: boolean;
  optionArray: T[];
  onSelect: (option: T) => void;
}

const SelectMenu = <T extends SelectMenuOption>({ search = true, optionArray, onSelect }: SelectMenuProps<T>) => {
  const { t } = useTranslation();
  const [searchKey, setSearchKey] = useState("");
  const itemRefs = useRef<(HTMLElement | null)[]>([]);

  const searchedOptions = optionArray.filter((item) => {
    const nameMatch = item?.name?.toLowerCase().includes(searchKey.toLowerCase());
    const idMatch = item?.id?.toString().includes(searchKey);
    return nameMatch || idMatch;
  });
  const isSuperAdmin = optionArray.some((item) => item.id === 0);

  const handleKeyDown = (e: React.KeyboardEvent, currentIndex = -1) => {
    if (e.key === "ArrowDown") {
      e.preventDefault();
      const nextIndex = currentIndex === -1 ? 0 : Math.min(currentIndex + 1, searchedOptions.length - 1);
      itemRefs.current[nextIndex]?.focus();
    } else if (e.key === "ArrowUp") {
      e.preventDefault();
      const prevIndex = currentIndex === -1 ? searchedOptions.length - 1 : Math.max(currentIndex - 1, 0);
      itemRefs.current[prevIndex]?.focus();
    } else if (e.key === "Enter" && currentIndex === -1 && searchedOptions.length > 0) {
      e.preventDefault();
      e.stopPropagation();
      setSearchKey("");
      onSelect(searchedOptions[0]);
    }
  };

  return (
    <>
      {search && (
        <StyledSearch
          placeholder={t("search")}
          value={searchKey}
          onChange={(e) => setSearchKey(e.target.value)}
          onKeyDown={handleKeyDown}
          variant="standard"
          autoFocus
          autoComplete="off"
        />
      )}
      <StyledList className="profile-menu">
        {searchedOptions.map((option, index) => {
          const handleSelect = (e: React.SyntheticEvent) => {
            e.preventDefault();
            e.stopPropagation();
            setSearchKey("");
            onSelect(option);
          };

          const handleKeyPress = (e: React.KeyboardEvent) => {
            handleKeyDown(e, index);
            if (e.key === "Enter") {
              handleSelect(e);
            }
          };

          return (
            <ListItemButton
              key={index}
              className="option-list-item"
              onMouseDown={handleSelect}
              onTouchEnd={handleSelect}
              onKeyDown={handleKeyPress}
              ref={(el) => {
                itemRefs.current[index] = el;
              }}
            >
              {option.name}
              {option.id !== undefined && ` (${option.id})`}
              {isSuperAdmin &&
                option.subscription !== "active" &&
                `, ${option.subscription === "inactive" ? t("no_subscription") : option.subscription}`}
            </ListItemButton>
          );
        })}
      </StyledList>
    </>
  );
};

interface BaseSelectionMenuProps {
  crew: Crew;
  onItemClick: (base: Base) => void;
  onCheckboxToggle: (base: Base) => void;
  onEnterWithChecked: () => void;
  onClose: () => void;
  className?: string;
}

const BaseSelectionMenu = ({
  crew,
  onItemClick,
  onCheckboxToggle,
  onEnterWithChecked,
  onClose,
  className = "option-list",
}: BaseSelectionMenuProps) => {
  const { t } = useTranslation();
  const selectedIds = crew?.baseIds || [];

  const allBases: Base[] = [{ id: "all", name: t("all_bases") }, ...(crew?.bases || [])];
  const itemRefs = useRef<(HTMLElement | null)[]>([]);

  useEffect(() => {
    itemRefs.current[0]?.focus();
  }, []);

  const handleKeyDown = (e: React.KeyboardEvent, currentIndex: number, base: Base) => {
    if (e.key === "ArrowDown") {
      e.preventDefault();
      const nextIndex = Math.min(currentIndex + 1, allBases.length - 1);
      itemRefs.current[nextIndex]?.focus();
    } else if (e.key === "ArrowUp") {
      e.preventDefault();
      const prevIndex = Math.max(currentIndex - 1, 0);
      itemRefs.current[prevIndex]?.focus();
    } else if (e.key === " ") {
      e.preventDefault();
      onCheckboxToggle(base);
    } else if (e.key === "Enter") {
      e.preventDefault();
      if (selectedIds.length > 0) {
        onEnterWithChecked();
      } else {
        onItemClick(base);
      }
    }
  };

  return (
    <ClickAwayListener onClickAway={onClose}>
      <div className={`${className} base-selection`}>
        <div className="base-selection-header">
          <Typography variant="subtitle2">
            {t("select_base_for")} {crew.name}
          </Typography>
        </div>
        <StyledList className="profile-menu">
          {allBases.map((base, index) => {
            const isSelected = base.id === "all" ? selectedIds.length === 0 : selectedIds.includes(base.id as number);
            return (
              <ListItemButton
                key={index}
                className="option-list-item"
                selected={isSelected}
                disableRipple
                tabIndex={0}
                sx={{ p: 0 }}
                onKeyDown={(e) => handleKeyDown(e, index, base)}
                ref={(el) => {
                  itemRefs.current[index] = el;
                }}
              >
                {base.id !== "all" && (
                  <span
                    style={{ display: "flex", alignItems: "center", padding: "4px 8px 4px 4px" }}
                    onMouseDown={(e) => {
                      e.preventDefault();
                      e.stopPropagation();
                      onCheckboxToggle(base);
                    }}
                    onTouchEnd={(e) => {
                      e.preventDefault();
                      e.stopPropagation();
                      onCheckboxToggle(base);
                    }}
                  >
                    <Checkbox
                      checked={isSelected}
                      size="small"
                      tabIndex={-1}
                      disableRipple
                      sx={{ flexShrink: 0, pointerEvents: "none" }}
                    />
                  </span>
                )}
                <span
                  style={{ flex: 1, padding: base.id === "all" ? "8px 16px" : "8px 16px 8px 0" }}
                  onMouseDown={(e) => {
                    e.preventDefault();
                    e.stopPropagation();
                    onItemClick(base);
                  }}
                  onTouchEnd={(e) => {
                    e.preventDefault();
                    e.stopPropagation();
                    onItemClick(base);
                  }}
                >
                  {base.name}
                </span>
              </ListItemButton>
            );
          })}
        </StyledList>
      </div>
    </ClickAwayListener>
  );
};

interface CompanySelectorProps {
  mobile?: boolean;
}

const CompanySelector = ({ mobile = false }: CompanySelectorProps) => {
  const { t } = useTranslation();
  const { crew, setCrew } = useCrew() as { crew: Crew; setCrew: (crew: Crew) => void };
  const user = useUser() as UserContext;
  const [isCrewMenuOpen, setIsCrewMenuOpen] = useState(false);
  const [selectedMainCrew, setSelectedMainCrew] = useState<Crew | null>(null);
  const [isBaseSelectionOpen, setIsBaseSelectionOpen] = useState(false);

  function selectCrew(selectedCrew: Crew) {
    if (!selectedCrew) return;

    if (selectedCrew.bases && selectedCrew.bases.length > 1) {
      const isSameCrew = crew?.id === selectedCrew.id;
      const existingBaseIds = isSameCrew ? (crew?.baseIds ?? null) : null;
      const existingBaseName = isSameCrew ? (crew?.baseName ?? null) : null;
      setSelectedMainCrew({ ...selectedCrew, baseIds: existingBaseIds, baseName: existingBaseName });
      setIsBaseSelectionOpen(true);
      setIsCrewMenuOpen(false);
    } else {
      setCrew({ ...selectedCrew, baseIds: null, baseName: null });
      setIsCrewMenuOpen(false);
    }
  }

  function buildCrewWithBase(prev: Crew, newIds: number[]): Crew {
    if (!newIds?.length) return { ...prev, baseIds: null, baseName: null };
    const baseName = newIds
      .map((id) => prev.bases?.find((b) => b.id === id)?.name)
      .filter(Boolean)
      .join(", ");
    return { ...prev, baseIds: newIds, baseName: baseName || null };
  }

  function selectBase(base: Base) {
    if (!selectedMainCrew) return;
    const newCrew =
      base.id === "all"
        ? { ...selectedMainCrew, baseIds: null, baseName: null }
        : buildCrewWithBase(selectedMainCrew, [base.id as number]);
    setCrew(newCrew);
    setIsBaseSelectionOpen(false);
    setIsCrewMenuOpen(false);
    setSelectedMainCrew(null);
  }

  function commitAndClose() {
    if (selectedMainCrew) {
      const allBaseIds = (selectedMainCrew.bases || []).map((b) => b.id as number);
      const selectedIds = selectedMainCrew.baseIds || [];
      const allSelected = allBaseIds.length > 0 && allBaseIds.every((id) => selectedIds.includes(id));
      setCrew(allSelected ? { ...selectedMainCrew, baseIds: null, baseName: null } : selectedMainCrew);
    }
    setIsBaseSelectionOpen(false);
    setIsCrewMenuOpen(false);
    setSelectedMainCrew(null);
  }

  function toggleBase(base: Base) {
    if (!selectedMainCrew) return;
    if (base.id === "all") {
      setCrew({ ...selectedMainCrew, baseIds: null, baseName: null });
      setIsBaseSelectionOpen(false);
      setIsCrewMenuOpen(false);
      setSelectedMainCrew(null);
      return;
    }
    setSelectedMainCrew((prev) => {
      if (!prev) return prev;
      const currentIds = prev.baseIds || [];
      const alreadySelected = currentIds.includes(base.id as number);
      const newIds = alreadySelected ? currentIds.filter((id) => id !== base.id) : [...currentIds, base.id as number];
      return buildCrewWithBase(prev, newIds);
    });
  }

  function handleCrewMenuOpen() {
    const userCrews = user.crews || [];
    if (userCrews.length === 1 && (userCrews[0].bases?.length ?? 0) > 1) {
      const isSameCrew = crew?.id === userCrews[0].id;
      const existingBaseIds = isSameCrew ? (crew?.baseIds ?? null) : null;
      const existingBaseName = isSameCrew ? (crew?.baseName ?? null) : null;
      setSelectedMainCrew({ ...userCrews[0], baseIds: existingBaseIds, baseName: existingBaseName });
      setIsBaseSelectionOpen(true);
    } else {
      setIsCrewMenuOpen(!isCrewMenuOpen);
    }
  }

  if (mobile) {
    return (
      <div className="search-block">
        <Button
          title={t("show_companies")}
          icon={<img src={Search_Icon} alt={String(t("search"))} />}
          onClick={handleCrewMenuOpen}
          style={{ width: 30, height: 30 }}
        />

        {isCrewMenuOpen && (
          <ClickAwayListener
            mouseEvent="onMouseDown"
            touchEvent="onTouchStart"
            onClickAway={() => setIsCrewMenuOpen(false)}
          >
            <div className="search-list">
              <SelectMenu optionArray={user.crews || []} onSelect={selectCrew} />
            </div>
          </ClickAwayListener>
        )}

        {isBaseSelectionOpen && selectedMainCrew && (
          <BaseSelectionMenu
            crew={selectedMainCrew}
            onItemClick={selectBase}
            onCheckboxToggle={toggleBase}
            onEnterWithChecked={commitAndClose}
            onClose={commitAndClose}
            className="search-list"
          />
        )}
      </div>
    );
  }

  return (
    <div className="infoblock">
      <div className="infoblock-column">
        <label>{t("company")}</label>
        <p>{crew.status === "active" && `${crew.name}${crew.baseName ? ` — ${crew.baseName}` : ""} (${crew.id})`}</p>
      </div>

      <Button
        title={t("show_companies")}
        icon={<MoreVertIcon />}
        onClick={handleCrewMenuOpen}
        style={{ width: 30, height: 30 }}
      />

      {isCrewMenuOpen && (
        <ClickAwayListener onClickAway={() => setIsCrewMenuOpen(false)}>
          <div className="option-list">
            <SelectMenu optionArray={user.crews || []} onSelect={selectCrew} />
          </div>
        </ClickAwayListener>
      )}

      {isBaseSelectionOpen && selectedMainCrew && (
        <BaseSelectionMenu
          crew={selectedMainCrew}
          onItemClick={selectBase}
          onCheckboxToggle={toggleBase}
          onEnterWithChecked={commitAndClose}
          onClose={commitAndClose}
        />
      )}
    </div>
  );
};

export default CompanySelector;
